'use client'

import { useState, useEffect } from 'react'
import { FileText, Users, Settings, Bell, Download, Search, Eye, Plus, Edit, Trash2, X } from 'lucide-react'
import AdminBriefView from './AdminBriefView'
import ManagersList from './ManagersList'
import UsersList from './UsersList'
import NotificationsPanel from './NotificationsPanel'

interface Brief {
  id: string
  objective: string | null
  targetAudience: string | null
  desiredReaction: string | null
  keyMessage: string | null
  tone: string | null
  status: string
  createdAt: string
  reference?: string | null
  user: {
    username: string
    email: string
    companyName: string | null
  }
  companyProfile: {
    companyName: string
    industry: string
  } | null
}

import { Language } from '@/lib/brief613/translations'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface AdminDashboardProps {
  userId: string
  language?: Language
}

type Tab = 'briefs' | 'users' | 'managers' | 'notifications'

export default function AdminDashboard({ userId, language = 'en' }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<Tab>('briefs')
  const [briefs, setBriefs] = useState<Brief[]>([])
  const [filteredBriefs, setFilteredBriefs] = useState<Brief[]>([])
  const [loading, setLoading] = useState(true)
  const [userRole, setUserRole] = useState<string>('')

  useEffect(() => {
    // Verify user role
    const storedRole = localStorage.getItem('brief613_userRole')
    setUserRole(storedRole || '')
    
    if (storedRole !== 'admin') {
      console.warn('[AdminDashboard] User role is not admin:', storedRole)
    }
  }, [])
  const [searchQuery, setSearchQuery] = useState('')
  const [objectiveFilter, setObjectiveFilter] = useState('')
  const [toneFilter, setToneFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('submitted') // Default to "en attente"
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedBriefId, setSelectedBriefId] = useState<string | null>(null)
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    in_progress: 0,
    completed: 0,
    thisMonth: 0,
    totalClients: 0,
  })
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)

  useEffect(() => {
    loadData()
    loadUnreadNotificationCount()
  }, [userId])

  // Reload notification count when switching to notifications tab
  useEffect(() => {
    if (activeTab === 'notifications') {
      loadUnreadNotificationCount()
    }
  }, [activeTab])

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadUnreadNotificationCount()
    }, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
  }, [userId])

  const loadUnreadNotificationCount = async () => {
    try {
      const response = await fetch('/api/brief613/notifications?unread_only=true', {
        headers: {
          'x-user-id': userId,
        },
      })

      if (response.ok) {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json()
          if (data.success) {
            setUnreadNotificationCount(data.notifications?.length || 0)
          }
        }
      }
    } catch (error) {
      console.error('Error loading unread notification count:', error)
    }
  }

  useEffect(() => {
    filterBriefs()
  }, [searchQuery, objectiveFilter, toneFilter, statusFilter, dateFrom, dateTo, briefs])

  const loadData = async () => {
    try {
      const [briefsRes, usersRes] = await Promise.all([
        fetch('/api/brief613/briefs', {
          headers: {
            'x-user-id': userId,
            'x-user-role': 'admin',
          },
        }),
        fetch('/api/brief613/users', {
          headers: {
            'x-user-id': userId,
            'x-user-role': 'admin',
          },
        }),
      ])

      const briefsData = await briefsRes.json()
      const usersData = await usersRes.json()

      if (briefsData.success) {
        setBriefs(briefsData.briefs)
        setFilteredBriefs(briefsData.briefs)

        // Calculate stats
        const today = new Date()
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const thisMonth = briefsData.briefs.filter(
          (b: Brief) => new Date(b.createdAt) >= firstDayOfMonth
        ).length

        const stats = {
          total: briefsData.briefs.length,
          submitted: briefsData.briefs.filter((b: Brief) => b.status === 'submitted').length,
          in_progress: briefsData.briefs.filter((b: Brief) => b.status === 'in_progress').length,
          completed: briefsData.briefs.filter((b: Brief) => b.status === 'completed').length,
          thisMonth,
          totalClients: usersData.success ? usersData.users.length : 0,
        }
        setStats(stats)
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const filterBriefs = () => {
    let filtered = [...briefs]

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (brief) =>
          brief.objective?.toLowerCase().includes(query) ||
          brief.user.username.toLowerCase().includes(query) ||
          brief.user.email.toLowerCase().includes(query) ||
          brief.companyProfile?.companyName.toLowerCase().includes(query) ||
          brief.companyProfile?.industry.toLowerCase().includes(query)
      )
    }

    // Objective filter
    if (objectiveFilter) {
      filtered = filtered.filter((brief) => brief.objective === objectiveFilter)
    }

    // Tone filter
    if (toneFilter) {
      filtered = filtered.filter((brief) => brief.tone === toneFilter)
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((brief) => brief.status === statusFilter)
    }

    // Date filters
    if (dateFrom) {
      const fromDate = new Date(dateFrom)
      filtered = filtered.filter((brief) => new Date(brief.createdAt) >= fromDate)
    }

    if (dateTo) {
      const toDate = new Date(dateTo)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter((brief) => new Date(brief.createdAt) <= toDate)
    }

    setFilteredBriefs(filtered)
  }

  const handleExportCSV = async () => {
    try {
      const response = await fetch('/api/brief613/briefs/export/csv', {
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `briefs_export_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }
    } catch (error) {
      console.error('Error exporting CSV:', error)
      alert('Failed to export CSV')
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setObjectiveFilter('')
    setToneFilter('')
    setStatusFilter('submitted') // Reset to default
    setDateFrom('')
    setDateTo('')
  }

  const hasActiveFilters = searchQuery || objectiveFilter || toneFilter || (statusFilter && statusFilter !== 'submitted') || dateFrom || dateTo

  // Get unique objectives and tones for filters
  const uniqueObjectives = Array.from(new Set(briefs.map((b) => b.objective).filter((obj): obj is string => Boolean(obj))))
  const uniqueTones = Array.from(new Set(briefs.map((b) => b.tone).filter((tone): tone is string => Boolean(tone))))

  if (selectedBriefId) {
    return (
      <AdminBriefView
        briefId={selectedBriefId}
        userId={userId}
        onBack={() => {
          setSelectedBriefId(null)
          loadData()
        }}
      />
    )
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 max-w-6xl mx-auto text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/55">Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with Tabs */}
      <div className={`${panel} p-6`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-base font-semibold text-white/90 sm:text-lg">Admin Dashboard</h2>
            {userRole && (
              <p className="text-white/60 text-sm mt-1">Role: <span className="text-white/55 font-semibold">{userRole}</span></p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.total}</p>
            <p className="text-white/45 text-xs">Total Briefs</p>
          </div>
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.thisMonth}</p>
            <p className="text-white/45 text-xs">This Month</p>
          </div>
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.totalClients}</p>
            <p className="text-white/45 text-xs">Active Clients</p>
          </div>
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.submitted}</p>
            <p className="text-white/45 text-xs">Submitted</p>
          </div>
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.in_progress}</p>
            <p className="text-white/45 text-xs">In Progress</p>
          </div>
          <div className={`${panelInner} p-4`}>
            <p className="text-xl font-semibold text-white/90">{stats.completed}</p>
            <p className="text-white/45 text-xs">Completed</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10">
          <button
            onClick={() => setActiveTab('briefs')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'briefs'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white/55'
            }`}
          >
            <FileText size={18} className="inline mr-2" />
            Briefs
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'users'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white/55'
            }`}
          >
            <Users size={18} className="inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('managers')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'managers'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white/55'
            }`}
          >
            <Settings size={18} className="inline mr-2" />
            Managers
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 font-medium transition-colors relative ${
              activeTab === 'notifications'
                ? 'text-white border-b-2 border-white'
                : 'text-white/60 hover:text-white/55'
            }`}
          >
            <Bell size={18} className="inline mr-2" />
            Notifications
            {unreadNotificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold text-white border-2 border-white/20">
                {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'briefs' && (
        <>
          {/* Filters */}
          <div className={`${panel} p-6`}>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search company..."
                    className={`${fieldClass} pl-10`}
                  />
                </div>
              </div>
              <div>
                <select
                  value={objectiveFilter}
                  onChange={(e) => setObjectiveFilter(e.target.value)}
                  className={fieldClass}
                >
                  <option value="">All Objectives</option>
                  {uniqueObjectives.map((obj) => (
                    <option key={obj} value={obj}>
                      {obj}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={toneFilter}
                  onChange={(e) => setToneFilter(e.target.value)}
                  className={fieldClass}
                >
                  <option value="">All Tones</option>
                  {uniqueTones.map((tone) => (
                    <option key={tone} value={tone}>
                      {tone}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={fieldClass}
                >
                  <option value="">All Status</option>
                  <option value="submitted">En attente (Submitted)</option>
                  <option value="in_progress">In Progress</option>
                  <option value="in_review">In Review</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              <div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={fieldClass}
                  placeholder="From"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={fieldClass}
                  placeholder="To"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={handleExportCSV} className={ghostBtn}>
                <Download size={16} className="text-white/50" />
                Export CSV
              </button>
              {hasActiveFilters && (
                <button onClick={clearFilters} className={ghostBtn}>
                  <X size={16} className="text-white/50" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Briefs List */}
          <div className={`${panel} p-6`}>
            <h3 className="text-base font-semibold text-white/90 mb-4">
              All Briefs ({filteredBriefs.length})
            </h3>
            {filteredBriefs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto mb-4 text-white/30" size={32} />
                <p className="text-white/70">No briefs found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBriefs.map((brief) => (
                  <div
                    key={brief.id}
                    className={`${panelInner} p-4 border-l-2 border-l-white/20 cursor-pointer hover:bg-white/5 transition-colors`}
                    onClick={() => setSelectedBriefId(brief.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-white/90 mb-1">
                          {brief.objective || 'Untitled Brief'}
                        </h4>
                        {brief.reference && (
                          <p className="text-xs text-white/50 mb-1 font-mono">
                            Reference: <span className="text-white/60">{brief.reference}</span>
                          </p>
                        )}
                        <p className="text-sm text-white/60 mb-2">
                          {brief.companyProfile?.companyName || brief.user.companyName || 'No company'} •{' '}
                          {brief.companyProfile?.industry || 'N/A'} • {brief.user.username}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-white/50">
                          <span>{new Date(brief.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full border ${
                            brief.status === 'submitted'
                              ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
                              : brief.status === 'in_progress'
                              ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
                              : brief.status === 'completed'
                              ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
                              : 'bg-white/5 text-white/50 border-white/10'
                          }`}
                        >
                          {brief.status}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedBriefId(brief.id)
                          }}
                          className="text-white/60 hover:text-white"
                        >
                          <Eye size={18} className="text-white/50" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'users' && <UsersList userId={userId} />}

      {activeTab === 'managers' && <ManagersList userId={userId} language={language} />}

      {activeTab === 'notifications' && (
        <NotificationsPanel 
          userId={userId} 
          onNotificationRead={() => loadUnreadNotificationCount()} 
        />
      )}
    </div>
  )
}
